"use client";

import { Button } from "@/components/atoms/buttons";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import NotificationSlider from "@/components/molecules/notification-slider/NotificationSlider";
import PaymentProductCard from "@/components/organisms/payment-product-card";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { ArrowUpRight, Loader2, Trash2 } from "lucide-react";
import Pagination from "@/components/ui/pagination";
import Link from "next/link";
import { useCartProducts, ITEMS_PER_PAGE } from "@/hooks/useCartProducts";
import { Content } from "@prismicio/client";

type CartProductsProps = {
  locale: Locales;
  notifications: Content.SiteSettingsDocumentDataNotificationsItem[];
};

export const MAX_VISIBLE_PAGES = 5;

const CartProducts = ({ locale, notifications }: CartProductsProps) => {
  const {
    itemCount,
    isModalOpen,
    setIsModalOpen,
    handleClearCart,
    isClearingCart,
    shouldShowLoader,
    currentPageItems,
    currentPage,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    goToNextPage,
    goToPrevPage,
    goToPage,
  } = useCartProducts(locale);

  return (
    <section className="space-y-4">
      {notifications.length > 0 && (
        <div className="bg-festi-pink-50 shrink-0 rounded-lg border px-6.5 py-5 sm:ml-auto sm:border-none">
          <NotificationSlider
            notifications={notifications}
            locale={locale}
            itemClassName="text-left"
            contentWidth="100%"
          />
        </div>
      )}

      {shouldShowLoader ? (
        <div className="mt-4 flex items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between sm:items-center">
            <h2 className="sm:font-cabinet-grotesk text-lg font-medium max-md:mt-4 sm:text-3xl">
              {itemCount >= 1 ? __("Items in cart", locale) + ` (${itemCount})` : __("No items in cart", locale)}
            </h2>
            {itemCount >= 1 && (
              <ConfirmDelete
                locale={locale}
                onClick={handleClearCart}
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                isActionHappening={isClearingCart}
                modalTrigger={
                  <div>
                    <Button
                      variant="secondary"
                      disabled={isClearingCart}
                      className="bg-inherit max-sm:hidden"
                      size="md"
                    >
                      {__("Delete all", locale)}
                      <Trash2 size={20} aria-hidden="true" />
                    </Button>
                    <button
                      disabled={isClearingCart}
                      className="group cursor-pointer rounded-full p-1 transition-all duration-300 ease-in-out sm:hidden"
                    >
                      <Trash2
                        size={20}
                        aria-hidden="true"
                        className="group-hover:text-pink-primary text-dark-primary"
                      />
                    </button>
                  </div>
                }
                title={__("Delete cart", locale)}
                text={__("Are you sure you want to delete the cart?", locale)}
              />
            )}
          </div>
          {itemCount === 0 && (
            <p className="font-cabinet-grotesk -mt-2 flex items-center gap-1 text-lg">
              {__("Explore our", locale)}{" "}
              <Link
                href={getPermalink(locale, "catalogue")}
                className="text-pink-primary link-effect relative flex items-center font-medium transition-all duration-300 ease-in-out hover:scale-[102%]"
              >
                {__("catalogue", locale)} <ArrowUpRight />
              </Link>
            </p>
          )}

          {currentPageItems.length >= 1 && (
            <>
              <ul className="max-md:space-y-2.5 min-md:grid min-md:grid-cols-1 min-md:gap-4 min-md:border-t min-md:pt-4">
                {currentPageItems.map(({ cartItem, product, prismicProduct }) => (
                  <PaymentProductCard
                    locale={locale}
                    key={cartItem.id}
                    medusaProduct={product}
                    prismicProduct={prismicProduct}
                    cartItem={cartItem}
                    quantity={cartItem.quantity}
                  />
                ))}
              </ul>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  goToPrevPage={goToPrevPage}
                  goToNextPage={goToNextPage}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  locale={locale}
                  goToPage={goToPage}
                  totalPages={totalPages}
                />
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default CartProducts;
